import { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import type { AuthorizationParams } from '@auth0/auth0-react';

import AuthenticationGuard from 'components/guards/AuthenticationGuard';
import CurrentUserGuard from 'components/guards/CurrentUserGuard';
import OnboardingGuard from 'components/guards/OnboardingGuard';
import UserOrganizationGuard from 'components/guards/UserOrganizationGuard';
import VerifyEmailGuard from 'components/guards/VerifyEmailGuard';
import AuthenticationProvider from 'components/providers/AuthenticationProvider';
import ThemeProvider from 'components/providers/ThemeProvider';

import useCurrentOrganization from 'hooks/useCurrentOrganization';

import Layout from 'layouts/Layout';
import OnboardingLayout from 'layouts/OnboardingLayout';

import AirspacePage from 'pages/AirspacePage';
import AllDataPage from 'pages/AllDataPage';
import ApplicationDetailsPage from 'pages/ApplicationDetailsPage';
import ApplicationsPage from 'pages/ApplicationsPage';
import AssetsPage from 'pages/AssetsPage';
import BatteryDetailsPage from 'pages/BatteryDetailsPage';
import BoundariesPage from 'pages/BoundariesPage';
import BoundaryDetailPage from 'pages/BoundaryDetailPage';
import ChemicalDetailPage from 'pages/ChemicalDetailPage';
import ChemicalsPage from 'pages/ChemicalsPage';
import ClientsDetailsPage from 'pages/ClientDetailsPage';
import ClientsPage from 'pages/ClientsPage';
import ConnectionsPage from 'pages/ConnectionsPage';
import CreateMixPage from 'pages/CreateMixPage';
import DroneDetailsPage from 'pages/DroneDetailsPage';
import FarmDetailsPage from 'pages/FarmDetailsPage';
import FarmsPage from 'pages/FarmsPage';
import FieldDetailsPage from 'pages/FieldDetailsPage';
import FieldsPage from 'pages/FieldsPage';
import HomePage from 'pages/HomePage';
import ImportPage from 'pages/ImportPage';
import JohnDeereCallbackPage from 'pages/JohnDeereCallbackPage';
import LoginPage from 'pages/LoginPage';
import LogoutPage from 'pages/LogoutPage';
import MaintenancePage from 'pages/MaintenancePage';
import MapTest from 'pages/MapTest';
import MixApplyPage from 'pages/MixApplyPage';
import ApplyFromApplicationRoute from 'pages/MixApplyPage/components/ApplyFromApplicationRoute';
import ApplyFromMixRoute from 'pages/MixApplyPage/components/ApplyFromMixRoute';
import MixApplyPreviewRoute from 'pages/MixApplyPage/components/MixApplyPreviewRoute';
import OnboardingCurrentUserPage from 'pages/OnboardingCurrentUserPage';
import OnboardingOrganizationCreationPage from 'pages/OnboardingOrganizationCreation';
import PrescriptionImportPage from 'pages/PrescriptionImportPage';
import PrivacyPolicyPage from 'pages/PrivacyPolicyPage';
import ReportsPage from 'pages/ReportsPage';
import SignupPage from 'pages/SignupPage';
import SupportPage from 'pages/SupportPage';
import TermsOfServicePage from 'pages/TermsOfServicePage';
import UsersDetailsPage from 'pages/UserDetailsPage';
import UsersPage from 'pages/UsersPage';
import VerifyEmailPage from 'pages/VerifyEmailPage';

const VisualPlayground = import.meta.env.DEV
    ? lazy(() => import('pages/VisualPlayground'))
    : () => null;

const AppRoutes = () => {
    const { currentOrganization } = useCurrentOrganization();

    const authorizationParams: AuthorizationParams = useMemo(
        () => ({
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            redirect_uri: window.location.origin,
            scope: 'openid profile email read:current_user',
            ...(currentOrganization?.auth0Id && { organization: currentOrganization.auth0Id })
        }),
        [currentOrganization]
    );

    return (
        <Routes>
            <Route
                element={
                    <AuthenticationProvider
                        authorizationParams={authorizationParams}
                        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
                        domain={import.meta.env.VITE_AUTH0_DOMAIN}
                    />
                }
                path='/'>
                <Route element={<LoginPage />} path='login' />

                <Route element={<LogoutPage />} path='logout' />

                <Route element={<SignupPage />} path='signup' />

                <Route element={<ThemeProvider value='default' />}>
                    <Route element={<TermsOfServicePage />} path='terms-of-service' />

                    <Route element={<PrivacyPolicyPage />} path='privacy-policy' />

                    <Route
                        element={
                            <Suspense fallback={null}>
                                <VisualPlayground />
                            </Suspense>
                        }
                        path='/_visual'
                    />
                </Route>

                <Route element={<AuthenticationGuard />}>
                    <Route element={<VerifyEmailGuard />}>
                        <Route element={<CurrentUserGuard />}>
                            <Route element={<UserOrganizationGuard />}>
                                <Route element={<ThemeProvider value='default' />}>
                                    <Route element={<Layout />}>
                                        <Route element={<HomePage />} index />

                                        <Route path='/data'>
                                            <Route
                                                element={<Navigate replace to='applications' />}
                                                index
                                            />

                                            <Route element={<AllDataPage />} path='allData' />

                                            <Route
                                                element={<ApplicationsPage />}
                                                path='applications'
                                            />

                                            <Route element={<AssetsPage />} path='assets' />

                                            <Route element={<ClientsPage />} path='clients' />

                                            <Route element={<FarmsPage />} path='farms' />

                                            <Route element={<FieldsPage />} path='fields' />

                                            <Route element={<BoundariesPage />} path='boundaries' />

                                            <Route element={<UsersPage />} path='users' />
                                        </Route>

                                        <Route
                                            element={<ApplicationDetailsPage />}
                                            path='data/applicationDetails/:applicationId'
                                        />

                                        <Route
                                            element={<ClientsDetailsPage />}
                                            path='data/clientDetails/:clientId'
                                        />

                                        <Route
                                            element={<FarmDetailsPage />}
                                            path='data/farmDetails/:farmId'
                                        />

                                        <Route
                                            element={<FieldDetailsPage />}
                                            path='data/fieldDetails/:fieldId'
                                        />

                                        <Route
                                            element={<UsersDetailsPage />}
                                            path='data/userDetails/:userId'
                                        />

                                        <Route
                                            element={<BatteryDetailsPage />}
                                            path='data/batteryDetails/:batteryId'
                                        />

                                        <Route
                                            element={<DroneDetailsPage />}
                                            path='data/droneDetails/:droneId'
                                        />

                                        <Route
                                            element={<BoundaryDetailPage />}
                                            path='data/boundaryDetails/:boundaryId'
                                        />

                                        <Route
                                            element={<PrescriptionImportPage />}
                                            path='data/prescriptions/new'
                                        />

                                        <Route path='/chemicals'>
                                            <Route element={<ChemicalsPage />} index />

                                            <Route
                                                element={<ChemicalDetailPage />}
                                                path='details/:chemicalId'
                                            />

                                            <Route element={<CreateMixPage />} path='mixes/new' />

                                            <Route
                                                element={<CreateMixPage />}
                                                path='mixes/:mixId'
                                            />

                                            <Route
                                                element={<MixApplyPage />}
                                                path='apply/:mixRecipeId'
                                            />

                                            <Route
                                                element={<ApplyFromMixRoute />}
                                                path='mix-apply/:mixId'
                                            />

                                            <Route
                                                element={<ApplyFromApplicationRoute />}
                                                path='mix-apply/app/:applicationId'
                                            />

                                            <Route
                                                element={<MixApplyPreviewRoute />}
                                                path='apply-preview/:variant'
                                            />
                                        </Route>

                                        <Route element={<ConnectionsPage />} path='/connections' />

                                        <Route element={<ImportPage />} path='/import' />

                                        <Route element={<MaintenancePage />} path='/maintenance' />

                                        <Route
                                            element={<MaintenancePage phase={1} />}
                                            path='/maintenance-preview-phase1'
                                        />

                                        <Route
                                            element={<MaintenancePage phase={2} />}
                                            path='/maintenance-preview-phase2'
                                        />

                                        <Route
                                            element={<MaintenancePage phase={3} />}
                                            path='/maintenance-preview-phase3'
                                        />

                                        <Route
                                            element={<MaintenancePage phase={4} />}
                                            path='/maintenance-preview-phase4'
                                        />

                                        <Route
                                            element={<MaintenancePage phase={5} />}
                                            path='/maintenance-preview-phase5'
                                        />

                                        <Route
                                            element={<MaintenancePage phase={6} />}
                                            path='/maintenance-preview-phase6'
                                        />

                                        <Route element={<AirspacePage />} path='/air' />

                                        <Route
                                            element={<JohnDeereCallbackPage />}
                                            path='/account/john-deere/callback'
                                        />

                                        <Route element={<ReportsPage />} path='reports' />

                                        <Route element={<MapTest />} path='/mapTest' />

                                        <Route element={<SupportPage />} path='/support' />
                                    </Route>
                                </Route>
                            </Route>
                        </Route>
                    </Route>

                    <Route element={<VerifyEmailGuard />}>
                        <Route element={<OnboardingGuard />}>
                            <Route element={<ThemeProvider value='onboarding' />}>
                                <Route element={<OnboardingLayout />} path='onboarding'>
                                    <Route element={<VerifyEmailPage />} path='verify/email' />

                                    <Route
                                        element={<OnboardingCurrentUserPage />}
                                        path='currentUser'
                                    />

                                    <Route
                                        element={<OnboardingOrganizationCreationPage />}
                                        path='organizationCreation'
                                    />
                                </Route>
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Routes>
    );
};

export default AppRoutes;
